import React, { useState, useEffect, startTransition } from 'react'
import clsx from 'clsx'
import loadable from '@loadable/component'
import '@docsearch/css'

import { CustomLink } from '@/components/atoms/CustomLink/CustomLink'
import { Image } from '@/components/atoms/Image/Image'
import { ReactComponent as MenuIcon } from '@/assets/icons/menu.svg'
import LogIn from './LogIn'

import { Props } from './types'
import * as styles from './HeaderSearch.module.css'

const DocSearch = loadable(() =>
  import('@docsearch/react').then((module) => ({
    default: module.DocSearch
  }))
)

const MobileNavigation = loadable(() => import('./MobileNavigation'))

export function HeaderSearch({
  buttons,
  logo,
  currentLang,
  languages,
  search_placeholder,
  search_no_results,
  suggestions_title,
  search_title,
  resources
}: Props) {
  const [isOpen, setOpen] = useState(false)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const onScroll = () => setOffset(window.pageYOffset)

    window.removeEventListener('scroll', onScroll)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleOpen = () => {
    startTransition(() => {
      const { body } = document

      setOpen((val) => !val)
      body.classList.toggle('menu-open')
    })
  }

  return (
    <header
      className={clsx(
        `relative z-10 mx-auto p-24 laptop:px-90 
      flex justify-center items-center sticky top-0 left-0 bg-white`,
        offset > 0 ? 'border-b-1 border-gray' : ''
      )}
    >
      <div className="flex justify-between items-center md:justify-start max-w-90 w-full">
        <CustomLink
          is_external={false}
          link_url={{ cached_url: '/help/', id: 'logo-link', url: '', fieldtype: 'multilink', linktype: '' }}
          new_tab={false}
          className="lg:w-[25%] flex-shrink-0 lg:pr-20"
        >
          <Image fixed data={logo} options={{ width: 103 }} width={103} height={20} className="w-131" />
        </CustomLink>
        <div className="flex netbook:hidden">
          <MobileNavigation
            logo={logo}
            languages={languages}
            currentLang={currentLang}
            isOpen={isOpen}
            handleOpen={handleOpen}
            resources={resources.content}
            buttons={buttons}
            searchPlaceholder={search_placeholder}
            searchNoResults={search_no_results}
            sectionTitle={suggestions_title}
            language={currentLang}
            searchTitle={search_title}
          />
        </div>

        <div
          className={clsx(
            'relative flex w-full justify-end netbook:items-center netbook:justify-center tablet:mx-24 lg:mx-0 lg:w-[55%]',
            styles.searchHolder
          )}
        >
          <div className="hidden tablet:flex items-center w-full h-[52px]">
            <DocSearch
              appId={process.env.GATSBY_ALGOLIA_APP_ID || ''}
              indexName="beebole_docs"
              apiKey={process.env.GATSBY_ALGOLIA_ADMIN_API_KEY || ''}
              disableUserPersonalization
              transformItems={(items) =>
                items.filter((item) => !item.url.includes('#contentContainer') && !item.url.includes('#main'))
              }
            />
            <input id="docSearch" />
            <i className="fa fa-search absolute right-[10%] my-auto cursor-pointer" />
          </div>
          <div className="block tablet:hidden">
            <DocSearch
              appId={process.env.GATSBY_ALGOLIA_APP_ID || ''}
              indexName="beebole_docs"
              apiKey={process.env.GATSBY_ALGOLIA_ADMIN_API_KEY || ''}
              transformItems={(items) =>
                items.filter((item) => !item.url.includes('#contentContainer') && !item.url.includes('#main'))
              }
            />
          </div>
        </div>
        <div className="hidden netbook:flex netbook:items-center ml-16 lg:w-[25%]">
          <LogIn buttons={buttons} />
        </div>
        <button onClick={handleOpen} type="button" className="ml-auto netbook:hidden" aria-label="open menu">
          <MenuIcon />
        </button>
      </div>
    </header>
  )
}
